import React from 'react';
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { sql } from "@codemirror/lang-sql";
import { useFileContents } from './Util.tsx';
import { example_1_file, example_2_file, example_3_file, example_4_file } from './PythonCode.tsx';

type Problem = {
  python: string;
  sql: string;
  caption: string;
};
const sqlExample = `/*
  Find all employees at BIG stores.
  Sort by biggest store descending.
*/
SELECT e.*, s.square_feet
FROM employees e
JOIN stores s
  ON e.store_id = s.id
WHERE s.square_feet > 10000
ORDER BY s.square_feet DESC;
`;

export const CodeComparison: React.FC = () => {
  const [current, setCurrent] = React.useState(0);

  const problems: Problem[] = [
    {
      python: useFileContents(example_1_file),
      sql: sqlExample,
      caption: "Example 1: Not too bad",
    },
    {
      python: useFileContents(example_2_file),
      sql: sqlExample,
      caption: "Example 2: What if the data doesn't fit in memory? Are you sure there are no bugs?",
    },
    {
      python: useFileContents(example_3_file),
      sql: sqlExample,
      caption: "Example 3: What if the data is sharded across multiple nodes? Getting really complicated",
    },
  ];

  const prev = () => setCurrent((c) => (c > 0 ? c - 1 : problems.length - 1));
  const next = () => setCurrent((c) => (c < problems.length - 1 ? c + 1 : 0));

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0 }}>Data manipulation in your app code vs in your database</h3>
        <h5 style={{ margin: 0 }}>Python and equivalent SQL</h5>
      </div>
      <figure style={{ margin: 0, width: '100%' }}>
        <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
          <CodeMirror
            value={problems[current].python}
            extensions={[python(), EditorView.editable.of(false)]}
            height="500px"
            style={{ flex: 1, minWidth: 0 }}
            theme={'dark'}
          />

          <CodeMirror
            value={problems[current].sql}
            extensions={[sql(), EditorView.editable.of(false)]}
            height="500px"
            style={{ flex: 1, minWidth: 0 }}
            theme={'dark'}
          />
        </div>

        <figcaption
          style={{
            textAlign: "center",
            marginTop: "8px",
            fontSize: "0.9em",
            color: "#666",
          }}
        >
          {problems[current].caption}
        </figcaption>

        <div style={{ textAlign: "center", marginTop: "8px" }}>

          <button
            onClick={prev}
            disabled={current === 0}
            style={{
              width: "32px",
              height: "32px",
              fontSize: "16px",
              marginRight: "8px",
              padding: 0,
              borderRadius: "4px",
              cursor: current === 0 ? "not-allowed" : "pointer",
              opacity: current === 0 ? 0.5 : 1, // visually indicate disabled
            }}
          >
            ◀
          </button>
          <button
            onClick={next}
            disabled={current === problems.length - 1}
            style={{
              width: "32px",
              height: "32px",
              fontSize: "16px",
              padding: 0,
              borderRadius: "4px",
              cursor: current === problems.length - 1 ? "not-allowed" : "pointer",
              opacity: current === problems.length - 1 ? 0.5 : 1,
            }}
          >
            ▶
          </button>
        </div>

      </figure>
        <div style={{ textAlign: "center",paddingBottom: "8px" }}>
        <h5 style={{ margin: 0 }}>If you find yourself writing code that reads/writes data from files, you probably should be using a database.</h5>
      </div>
    </div>
  );
};